import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LifeBuoy, Mail, Phone, MapPin, MessageCircle, CreditCard, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const faqs = [
  {
    q: 'How do I add income or expenses?',
    a: 'Send a plain message like "paid 40 for fuel" or "got 500 from client" on WhatsApp, or add it in the app. MoneyFeed records and categorizes it for you.',
  },
  {
    q: 'How do I scan a receipt?',
    a: 'Open Receipts in the app and use your camera to snap the receipt, or send the photo on WhatsApp. We read the amount, date and merchant automatically.',
  },
  {
    q: 'How do I manage or cancel my subscription?',
    a: 'On iPhone/iPad, open the iOS Settings app → tap your name → Subscriptions → MoneyFeed. On web or Android, open Settings in the app to manage your plan.',
  },
  {
    q: 'How do I restore a purchase?',
    a: 'On iOS, open Settings → Subscription & plans and tap "Restore Purchases".',
  },
  {
    q: 'How do I export or delete my data?',
    a: 'Go to Settings → Export Data to download everything, or delete your account and all data from Settings (or at moneyfeed.io/delete-account).',
  },
  {
    q: 'How do I stop WhatsApp messages?',
    a: 'Reply STOP to any WhatsApp message from us to opt out, and START to opt back in. This does not delete your account or data.',
  },
];

const Support = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#D1FAE5]" data-testid="support-page">
      <div className="bg-[#064E3B] text-white py-6">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10 mb-4"
            data-testid="support-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold">Support</h1>
          <p className="text-gray-300 mt-2">We're here to help — most emails get a reply the same day.</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto space-y-8">

          {/* Contact card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <LifeBuoy className="h-8 w-8 text-[#064E3B]" />
              <h2 className="text-2xl font-bold text-[#064E3B]">Contact us</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <a
                href="mailto:feed@moneyfeed.io"
                className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="support-email-link"
              >
                <Mail className="h-5 w-5 text-[#84CC16] mt-0.5" />
                <div>
                  <p className="font-semibold text-[#064E3B]">Email</p>
                  <p className="text-sm text-gray-600 break-all">feed@moneyfeed.io</p>
                </div>
              </a>
              <a
                href="tel:+18326886863"
                className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="support-phone-link"
              >
                <Phone className="h-5 w-5 text-[#84CC16] mt-0.5" />
                <div>
                  <p className="font-semibold text-[#064E3B]">Phone</p>
                  <p className="text-sm text-gray-600">+1 (832) 688-6863</p>
                </div>
              </a>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <MapPin className="h-5 w-5 text-[#84CC16] mt-0.5" />
                <div>
                  <p className="font-semibold text-[#064E3B]">Address</p>
                  <p className="text-sm text-gray-600">5900 Balcones Drive 7274, Austin, TX 78731</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick links */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10">
            <h2 className="text-2xl font-bold text-[#064E3B] mb-6">Quick help</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <MessageCircle className="h-5 w-5 text-[#84CC16] mt-0.5" />
                <div>
                  <p className="font-semibold text-[#064E3B]">Log via WhatsApp</p>
                  <p className="text-sm text-gray-600">Message us to track income, expenses and receipts.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <CreditCard className="h-5 w-5 text-[#84CC16] mt-0.5" />
                <div>
                  <p className="font-semibold text-[#064E3B]">Subscriptions</p>
                  <p className="text-sm text-gray-600">Manage or cancel in your Apple Account (iOS) or in Settings (web/Android).</p>
                </div>
              </div>
              <a href="/delete-account" className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid="support-delete-link">
                <Trash2 className="h-5 w-5 text-[#84CC16] mt-0.5" />
                <div>
                  <p className="font-semibold text-[#064E3B]">Delete account</p>
                  <p className="text-sm text-gray-600">Remove your account and data anytime.</p>
                </div>
              </a>
            </div>
          </div>

          {/* FAQ */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-10">
            <h2 className="text-2xl font-bold text-[#064E3B] mb-6">Frequently asked questions</h2>
            <div className="space-y-6">
              {faqs.map((f, i) => (
                <div key={i} data-testid={`support-faq-${i}`}>
                  <h3 className="font-semibold text-[#064E3B] mb-1">{f.q}</h3>
                  <p className="text-gray-700 text-sm">{f.a}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Support;
